import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getTechnicianAvailabilityCalendar,
  postTechnicianAwayStatus,
  postTechnicianRecurringAvailability,
  putTechnicianAvailabilityCalendar,
} from 'queries';
import { parseErrors, Toast } from 'helpers';
import { JsonDataItem } from 'types';

export const CALENDAR_SAVED_MESSAGE = 'Calendar change has been saved.';
export const AVAILABILITY_SAVED_MESSAGE = 'Availability has been saved.';

const showError = (error: JsonDataItem) => {
  Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
};

export const useTechnicianAvailabilityCalendar = (technicianId: string | undefined, month: number, year: number) => {
  const {
    data: calendar,
    isLoading: isCalendarLoading,
    isFetching: isCalendarFetching,
    refetch: refetchCalendar,
  } = useQuery(['technician-availability-calendar', { technicianId, month, year }], getTechnicianAvailabilityCalendar, {
    enabled: !!technicianId,
    onError: showError,
  });

  const { mutate: updateSlots, isLoading: isSlotsUpdating } = useMutation(putTechnicianAvailabilityCalendar, {
    onSuccess: () => {
      Toast.show({ description: CALENDAR_SAVED_MESSAGE, type: Toast.Type.Success });
      return refetchCalendar();
    },
    onError: (error: JsonDataItem) => {
      showError(error);
      // the calendar was stale, so show the saved state again
      return refetchCalendar();
    },
  });

  const { mutate: createAwayStatus, isLoading: isAwayStatusCreating } = useMutation(postTechnicianAwayStatus, {
    onSuccess: () => {
      Toast.show({ description: AVAILABILITY_SAVED_MESSAGE, type: Toast.Type.Success });
      return refetchCalendar();
    },
    onError: showError,
  });

  const { mutate: createRecurringAvailability, isLoading: isRecurringAvailabilityCreating } = useMutation(
    postTechnicianRecurringAvailability,
    {
      onSuccess: () => {
        Toast.show({ description: AVAILABILITY_SAVED_MESSAGE, type: Toast.Type.Success });
        return refetchCalendar();
      },
      onError: showError,
    },
  );

  return {
    calendar,
    isCalendarLoading,
    isCalendarFetching,
    refetchCalendar,
    updateSlots,
    isSlotsUpdating,
    createAwayStatus,
    isAwayStatusCreating,
    createRecurringAvailability,
    isRecurringAvailabilityCreating,
  };
};
